import { CloseOutlined } from '@ant-design/icons';
import { Input } from '@mm/components';
import { useDebounce } from '@mm/hooks';
import React, { useEffect, useState } from 'react';
import { PrimaryButton } from '../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { CheckIcon } from '../../../../../../common/component-icons/CheckIcon/CheckIcon';
import { CloseModalProps } from '../../../../../../common/modals/modal-helpers';
import { colors } from '../../../styles/colors';
import { useAudienceNameValidation } from '../../Audiences/use-audience-name-validation';
import styles from './CreateMyLeadsAudienceModal.module.css';

export const CreateMyLeadsAudienceModal = ({
  onSave,
  closeModal,
  title,
  defaultName = '',
}: {
  onSave: (audienceName: string) => void;
  title?: string;
  defaultName?: string;
} & CloseModalProps) => {
  const [audienceName, setAudienceName] = useState(defaultName);
  const trimmedNewAudienceName = audienceName.trim();
  const debouncedNewAudienceName = useDebounce(trimmedNewAudienceName, 1000);
  const { validateAudienceName } = useAudienceNameValidation();
  const [valid, setValid] = useState(false);
  const [checking, setChecking] = useState(false);
  const [touched, setTouched] = useState(false);

  const onClickDone = () => {
    onSave(audienceName);
    closeModal();
  };

  useEffect(() => {
    setValid(false);
    setChecking(!!trimmedNewAudienceName);
  }, [trimmedNewAudienceName]);

  useEffect(() => {
    (async () => {
      setChecking(true);
      const valid = await validateAudienceName(debouncedNewAudienceName);
      setValid(valid);
      setChecking(false);
    })();
  }, [debouncedNewAudienceName, validateAudienceName]);

  return (
    <div className={styles.container}>
      <CloseOutlined
        className={styles.closeIcon}
        onClick={() => closeModal()}
      />
      <div className={styles.header}>
        <div className={styles.title}>{title || 'Name your new Audience'}</div>
      </div>
      <div className={styles.audienceNameWrapper}>
        <Input
          css={{
            input: styles.audienceEditBarInput,
            container: styles.audienceEditBar,
          }}
          value={audienceName}
          onChange={e => {
            setAudienceName(e.target.value);
            if (defaultName !== trimmedNewAudienceName) {
              setTouched(true);
            }
          }}
          invalid={!valid && !checking}
          isLoading={checking}
          icon={
            valid && !checking && trimmedNewAudienceName ? (
              <CheckIcon color={colors.green} size={16} />
            ) : undefined
          }
          placeholder="Enter name here..."
        />
        {!valid && !checking && touched && (
          <div className={styles.error}>
            An audience with that name already exists
          </div>
        )}
      </div>
      <div className={styles.buttons}>
        <PrimaryButton
          onClick={onClickDone}
          className={styles.primaryBtn}
          disabled={!valid || !audienceName}
        >
          Save
        </PrimaryButton>
      </div>
    </div>
  );
};
